import { z } from 'zod';
import { palierSchema } from './modules.js';
import { instantLocal, partiesLocales } from './prise-rendez-vous.js';
import { sirenValide } from './tax/identifiants.js';
const prixCentimes = z.number().int().min(1).max(10_000_000).nullable();
const motif = z.string().trim().min(1).max(500);
const revision = z.number().int().nonnegative();
export const schemaVersionPrixAddon = z.strictObject({
    mensuelHtCentimes: prixCentimes,
    annuelHtCentimes: prixCentimes,
    achatHtCentimes: prixCentimes,
    essaiJours: z.number().int().min(0).max(90),
    depuisLe: z.iso.datetime({ offset: true }),
    motif,
    revision,
});
export const schemaSuppressionPrixAddon = z.strictObject({ motif, revision });
export const schemaVersionPrixCoeur = z.strictObject({
    mensuelHtCentimes: prixCentimes,
    annuelHtCentimes: prixCentimes,
    depuisLe: z.iso.datetime({ offset: true }),
    motif,
    revision,
});
export const schemaResiliationCoeur = z.strictObject({ revision });
export const schemaResiliationCoeurConsole = schemaResiliationCoeur.extend({ motif });
export const schemaChangementPalierCoeur = z.strictObject({ palier: palierSchema, revision });
export const schemaChangementPalierCoeurConsole = schemaChangementPalierCoeur.extend({ motif });
/** Une date d'effet prime sur l'ordre de saisie des versions futures. */
export function prixEnVigueur(lignePrix, maintenant) {
    let retenue = null;
    for (const version of lignePrix.versions) {
        const date = new Date(version.depuisLe).getTime();
        if (date > maintenant.getTime())
            continue;
        if (!retenue ||
            date > new Date(retenue.depuisLe).getTime() ||
            (date === new Date(retenue.depuisLe).getTime() && version.version > retenue.version)) {
            retenue = version;
        }
    }
    return retenue;
}
/** La saisie décimale est découpée pour ne jamais arrondir un flottant. */
export function centimesDepuisEuros(texte) {
    if (!/^\d+(?:[.,]\d{1,2})?$/.test(texte))
        throw new RangeError('montant_euros_invalide');
    const [euros = '', decimales = ''] = texte.split(/[.,]/);
    const centimes = BigInt(euros) * 100n + BigInt(decimales.padEnd(2, '0'));
    if (centimes > BigInt(Number.MAX_SAFE_INTEGER))
        throw new RangeError('montant_euros_invalide');
    return Number(centimes);
}
export function periodeSuivante({ ancrageJour, debut, formule, }) {
    if (!Number.isInteger(ancrageJour) || ancrageJour < 1 || ancrageJour > 31) {
        throw new RangeError('ancrage_jour_invalide');
    }
    const locale = partiesLocales(debut, 'Europe/Paris').date;
    const [annee, mois] = locale.split('-').map(Number);
    const prochain = new Date(Date.UTC(annee, mois - 1 + (formule === 'annuelle' ? 12 : 1), 1));
    const dernierJour = new Date(Date.UTC(prochain.getUTCFullYear(), prochain.getUTCMonth() + 1, 0)).getUTCDate();
    const jour = String(Math.min(ancrageJour, dernierJour)).padStart(2, '0');
    const date = `${prochain.toISOString().slice(0, 7)}-${jour}`;
    const fin = instantLocal(date, '00:00', 'Europe/Paris');
    if (!fin)
        throw new RangeError('periode_invalide');
    return fin;
}
const paysIso = new Set(('AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ ' +
    'CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR ' +
    'GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP ' +
    'KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ ' +
    'NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW ' +
    'SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ ' +
    'UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW').split(' '));
const texteBorne = (max) => z.string().trim().min(1).max(max);
export const schemaFicheFacturation = z
    .strictObject({
    raisonSociale: texteBorne(200),
    adresse: z.strictObject({
        ligne1: texteBorne(200),
        ligne2: texteBorne(200).optional(),
        codePostal: texteBorne(20),
        ville: texteBorne(100),
        pays: z.string().refine((pays) => paysIso.has(pays), 'pays_iso_invalide'),
    }),
    siren: z.string().refine(sirenValide, 'siren_invalide').optional(),
    tvaIntracom: z
        .string()
        .regex(/^[A-Z]{2}[0-9A-Z]{2,12}$/)
        .optional(),
    emailFacturation: z.email().max(254),
})
    .superRefine((fiche, contexte) => {
    if (fiche.adresse.pays === 'FR' && !fiche.siren) {
        contexte.addIssue({ code: 'custom', path: ['siren'], message: 'siren_requis' });
    }
    const prefixes = { GR: 'EL', MC: 'FR' };
    const prefixe = prefixes[fiche.adresse.pays] ?? fiche.adresse.pays;
    if (fiche.tvaIntracom && !fiche.tvaIntracom.startsWith(prefixe)) {
        contexte.addIssue({
            code: 'custom',
            path: ['tvaIntracom'],
            message: 'prefixe_tva_incoherent',
        });
    }
});
export const schemaEcritureFicheFacturation = schemaFicheFacturation.safeExtend({
    revisionFicheFacturation: revision,
});
export const schemaVerificationTva = z.strictObject({ motif, revisionFicheFacturation: revision });
export const VERSION_CONDITIONS_VENTE_ADDONS = '2026-10-brouillon';
export const PAYS_UNION_EUROPEENNE = [
    'AT',
    'BE',
    'BG',
    'HR',
    'CY',
    'CZ',
    'DK',
    'EE',
    'FI',
    'FR',
    'DE',
    'GR',
    'HU',
    'IE',
    'IT',
    'LV',
    'LT',
    'LU',
    'MT',
    'NL',
    'PL',
    'PT',
    'RO',
    'SK',
    'SI',
    'ES',
    'SE',
];
//# sourceMappingURL=marketplace.js.map